import React, { forwardRef } from "react";
import Select from "react-select";

const CustomSelect = forwardRef(({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  className = "",
  placeholder = "Select",
  isSearchable = false,
  size, // 'compact' for smaller height in dense forms
  styles,
  menuPortalTarget,
  isLoading,
  isDisabled,
  ...props
}, ref) => {
  const isReactSelect = isSearchable || !!styles;

  if (isReactSelect) {
    const isCompact = size === 'compact';
    const isForm = size === 'form';
    // form: match standard input height (CustomInput px-2 py-2 + text-sm + border ≈ 38px);
    // compact: 28px table; default: 44px
    const controlHeight = isCompact ? '28px' : isForm ? '38px' : '44px';
    const defaultStyles = {
      control: (base, state) => ({
        ...base,
        minHeight: controlHeight,
        height: controlHeight,
        border: '1px solid #cbd5e1',
        borderRadius: '0.375rem',
        fontSize: '12px',
        color: '#000',
        backgroundColor: '#fff',
        boxShadow: state.isFocused ? '0 0 0 1px #e11d48' : 'none',
        '&:hover': { border: '1px solid #e11d48' }
      }),
      valueContainer: (base) => ({ ...base, padding: isCompact ? '0 6px' : isForm ? '0 8px' : '0 12px' }),
      singleValue: (base) => ({ ...base, color: '#000', fontSize: '12px' }),
      placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '12px' }),
      option: (base, state) => ({
        ...base,
        fontSize: '12px',
        backgroundColor: state.isSelected ? '#fff1f2' : state.isFocused ? '#fff1f2' : '#fff',
        color: '#000',
        cursor: 'pointer'
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      ...styles
    };

    return (
      <div className={`${className}`}>
        {label && (
           <label className="flex text-sm font-normal text-slate-500 mb-1 text-nowrap items-center gap-1">
            {label}
            {required && <span className="text-red-500 text-xl">*</span>}
          </label>
        )}
        <Select
          ref={ref}
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isSearchable={isSearchable}
          isLoading={isLoading}
          isDisabled={isDisabled}
          styles={defaultStyles}
          menuPortalTarget={menuPortalTarget}
          {...props}
        />
      </div>
    );
  }

  return (
    <div className={` ${className}`}>
      {label && (
        <label className="flex items-center gap-1 text-sm font-normal text-slate-500 mb-1">
          {label}
          {required ? (
            <span className="text-red-500 text-xl">*</span>
          ) : (
            <span className="text-xl">&nbsp;</span>
          )}
        </label>
      )}
      <select
        ref={ref}
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full h-11 border border-slate-300 rounded-md focus:border-flamingo-500 focus:ring-0 focus:outline-none transition-colors duration-200 bg-white text-[12px] text-black`}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
});

CustomSelect.displayName = "CustomSelect";

export default CustomSelect;
